import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import api from "../utils/axiosConfig";
import { toast } from "react-toastify";
import { suggestEmailCorrection } from "../utils/emailTypoHelper";
import "../styles/pages/Login.css"; // ✅ Same CSS used for uniform layout

const maskEmail = (email) => {
  if (!email || !email.includes("@")) return email || "";
  const [user, domain] = email.split("@");
  if (user.length <= 2) return `${user}••••@${domain}`;
  return `${user.slice(0, 2)}••••••@${domain}`;
};

const VerifyEmailOTP = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const targetEmail = location.state?.emailOrPhone || localStorage.getItem("pendingVerifyEmail") || "";
  const [form, setForm] = useState({ emailOrPhone: targetEmail, otp: "" });
  const [msg, setMsg] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendTimer, setResendTimer] = useState(30);

  // Email Edit / Correction State
  const [isEditingEmail, setIsEditingEmail] = useState(false);
  const [newEmailInput, setNewEmailInput] = useState("");
  const [newEmailSuggestion, setNewEmailSuggestion] = useState(null);
  const [changeEmailLoading, setChangeEmailLoading] = useState(false);

  useEffect(() => {
    let interval;
    if (resendTimer > 0) {
      interval = setInterval(() => {
        setResendTimer((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [resendTimer]);

  useEffect(() => {
    if (!targetEmail) {
      navigate("/register", { replace: true });
    } else {
      setForm((prev) => ({ ...prev, emailOrPhone: targetEmail }));
    }
  }, [targetEmail, navigate]);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleNewEmailChange = (e) => {
    const val = e.target.value;
    setNewEmailInput(val);
    const suggestion = suggestEmailCorrection(val);
    setNewEmailSuggestion(suggestion);
  };

  const applyNewEmailSuggestion = () => {
    if (newEmailSuggestion) {
      setNewEmailInput(newEmailSuggestion);
      setNewEmailSuggestion(null);
    }
  };

  const handleUpdateEmailSubmit = async (e) => {
    e.preventDefault();
    if (!newEmailInput.trim()) return;

    const directCheck = suggestEmailCorrection(newEmailInput);
    if (directCheck) {
      setNewEmailSuggestion(directCheck);
      toast.warning(`Did you mean "${directCheck}"? Click "Fix" to apply.`);
      return;
    }

    setChangeEmailLoading(true);
    setError("");
    setMsg("");

    try {
      const res = await api.post("/auth/change-unverified-email", {
        oldEmail: form.emailOrPhone,
        newEmail: newEmailInput.trim(),
      });

      const updated = res.data.newEmail || newEmailInput.trim();
      setForm((prev) => ({ ...prev, emailOrPhone: updated, otp: "" }));
      localStorage.setItem("pendingVerifyEmail", updated);
      setIsEditingEmail(false);
      setNewEmailInput("");
      setNewEmailSuggestion(null);
      setResendTimer(45);
      setMsg(`Email updated to ${updated}! A fresh verification code has been sent.`);
      toast.success("Email updated! Please check your inbox for the fresh OTP.");
    } catch (err) {
      const errMsg = err.response?.data?.message || "Failed to update email. Please try again.";
      setError(errMsg);
      toast.error(errMsg);
    } finally {
      setChangeEmailLoading(false);
    }
  };

  const handleResendOTP = async () => {
    if (!form.emailOrPhone) return;
    if (resendTimer > 0 || resendLoading) return;

    setError("");
    setMsg("");
    setResendLoading(true);

    try {
      await api.post("/auth/resend-verification-otp", { emailOrPhone: form.emailOrPhone });
      setMsg("A fresh verification OTP has been sent to your email!");
      setResendTimer(45);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to resend OTP. Please try again.");
    } finally {
      setResendLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMsg("");
    setError("");
    setLoading(true);
    try {
      const res = await api.post("/auth/verify-email-otp", form);

      setMsg(res.data.message || "OTP is correct!");

      // ✅ Save user and role in localStorage (Token is handled via HttpOnly Cookies now)
      if (res.data.user) {
        localStorage.setItem("user", JSON.stringify(res.data.user));
        localStorage.setItem("role", res.data.role); // ✅
        localStorage.removeItem("pendingVerifyEmail");
      }

      // ✅ Redirect to selfie page
      setTimeout(() => navigate("/selfie-capture"), 1500);
    } catch (err) {
      setError(err.response?.data?.message || "Problem verifying OTP!");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-container">
        {/* Left Side - Image + Quote */}
        <div className="a12">
          <div className="form-img">
            <img src="/images/login-banner.jpg" alt="banner" className="c" />
            <div className="logo-wrapper">
              <a
                href="https://Fotographiya.com"
                target="_blank"
                rel="noopener noreferrer"
              >
                <img
                  src="/images/logo_colored_white.png"
                  alt="Fotographiya"
                  width="1299"
                  height="192"
                />
              </a>
            </div>
            <div className="overlaytext" style={{ width: "520px" }}>
              <p>
                “Email verification is not just security, it's a beginning of
                trust.”
              </p>
              <h4 style={{ marginBottom: "5px" }}>Team Fotographiya</h4>
              <p style={{ fontSize: "12px", marginBottom: 0 }}>
                Community Support
              </p>
            </div>
          </div>
        </div>

        {/* Right Side - OTP Verification Form */}
        <div className="b12">
          <div className="login-form">
            <div className="headerlogin"></div>

            <div className="loginfrom-top">
              <h2>Verify your email</h2>
              <span className="verify-subtitle-clean">
                Enter the 6-digit code sent to <strong>{maskEmail(form.emailOrPhone)}</strong>. Not in your inbox? Check Spam or Promotions.
              </span>

              {/* ✏️ Wrong Email? Edit Email Option */}
              <div style={{ marginTop: "10px", marginBottom: "14px", display: "flex", alignItems: "center", justifyContent: "space-between", background: "#f8fafc", padding: "8px 12px", borderRadius: "8px", border: "1px dashed #cbd5e1" }}>
                <span style={{ fontSize: "12px", color: "#64748b" }}>
                  Entered wrong email address?
                </span>
                <button
                  type="button"
                  onClick={() => {
                    setIsEditingEmail(!isEditingEmail);
                    setNewEmailInput(form.emailOrPhone);
                    setNewEmailSuggestion(null);
                  }}
                  style={{
                    background: "none",
                    border: "none",
                    color: "#4f46e5",
                    fontWeight: "600",
                    fontSize: "12px",
                    cursor: "pointer",
                    textDecoration: "underline",
                    padding: 0
                  }}
                >
                  {isEditingEmail ? "Cancel" : "Change Email"}
                </button>
              </div>

              {/* Inline Change Email Box */}
              {isEditingEmail && (
                <div style={{ background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: "10px", padding: "12px", marginBottom: "16px" }}>
                  <p style={{ margin: "0 0 8px 0", fontSize: "12px", fontWeight: "600", color: "#1e40af" }}>
                    Enter your correct email address:
                  </p>
                  <input
                    type="email"
                    className="input-control"
                    placeholder="e.g. name@gmail.com"
                    value={newEmailInput}
                    onChange={handleNewEmailChange}
                    style={{ marginBottom: "8px", background: "#ffffff" }}
                  />

                  {newEmailSuggestion && (
                    <div
                      style={{
                        background: "#ffffff",
                        border: "1px solid #93c5fd",
                        borderRadius: "6px",
                        padding: "6px 10px",
                        marginBottom: "10px",
                        fontSize: "12px",
                        color: "#1d4ed8",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        cursor: "pointer"
                      }}
                      onClick={applyNewEmailSuggestion}
                    >
                      <span>Did you mean <strong>{newEmailSuggestion}</strong>?</span>
                      <button
                        type="button"
                        style={{ background: "#2563eb", color: "#fff", border: "none", borderRadius: "4px", padding: "2px 8px", fontSize: "11px", fontWeight: "bold", cursor: "pointer" }}
                        onClick={applyNewEmailSuggestion}
                      >
                        Fix
                      </button>
                    </div>
                  )}

                  <div style={{ display: "flex", gap: "8px" }}>
                    <button
                      type="button"
                      className="btn btn-primary"
                      style={{ flex: 1, padding: "8px 12px", fontSize: "12px" }}
                      disabled={changeEmailLoading || !newEmailInput.trim()}
                      onClick={handleUpdateEmailSubmit}
                    >
                      {changeEmailLoading ? "Updating..." : "Send OTP to New Email"}
                    </button>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      style={{ padding: "8px 12px", fontSize: "12px" }}
                      onClick={() => setIsEditingEmail(false)}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              <form onSubmit={handleSubmit}>
                <label style={{ width: "100%" }} htmlFor="emailOrPhone">
                  <span className="sr-only">Email Address</span>
                  <input
                    id="emailOrPhone"
                    className="input-control"
                    type="email"
                    name="emailOrPhone"
                    placeholder="Your Email Address"
                    value={form.emailOrPhone}
                    readOnly
                    required
                  />
                </label>

                <label style={{ width: "100%" }} htmlFor="otp">
                  <span className="sr-only">OTP</span>
                  <input
                    id="otp"
                    className="input-control"
                    type="text"
                    name="otp"
                    placeholder="OTP"
                    value={form.otp}
                    onChange={handleChange}
                    required
                  />
                </label>

                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "8px", marginBottom: "4px", padding: "0 4px" }}>
                  <span style={{ fontSize: "13px", color: "#64748b" }}>Didn't receive code?</span>
                  <button
                    type="button"
                    onClick={handleResendOTP}
                    disabled={resendTimer > 0 || resendLoading}
                    style={{
                      background: "none",
                      border: "none",
                      color: resendTimer > 0 ? "#94a3b8" : "#6366f1",
                      fontWeight: "600",
                      fontSize: "13px",
                      cursor: resendTimer > 0 ? "not-allowed" : "pointer",
                      padding: "4px 6px",
                      textDecoration: resendTimer > 0 ? "none" : "underline",
                    }}
                  >
                    {resendLoading ? "Sending..." : resendTimer > 0 ? `Resend OTP (${resendTimer}s)` : "Resend OTP"}
                  </button>
                </div>

                <button className="btn btn-primary-gradient login-btn" type="submit" disabled={loading}>
                  {loading ? "Checking OTP..." : "Verify OTP"}
                </button>
              </form>

              {/* Messages */}
              {msg && <p className="success-message">{msg}</p>}
              {error && <p className="error-message">{error}</p>}
            </div>

            <div className="footerlogin"></div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VerifyEmailOTP;
